import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import IdleTimer from "react-idle-timer";
import { Modal } from "react-responsive-modal";
import { useDispatch, connect } from "react-redux";
import * as Actions from "../../store/actions/global/global";
import refreshToken, {checkNonQADomain, checkImpersonateUser} from "../../util/util";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { CustomisedTertiaryButton, CPButton, Wrapper, TextWrapper } from "../../constants/common/StyledGlobalConstants";
import CPIcon from "./CPIcon";
import "../../components/common/RouterPrompt.css";
import PropTypes from "prop-types";
//START - REDBALL CONFIG
import { useAuth0 } from "@auth0/auth0-react";
import { REDBALL_FEATURE_TOGGLE } from "../../constants/appConstants";
//END - REDBALL CONFIG
const MobileBreakPoint = 480;

function IdleTimerContainer(props) {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const navigate = useNavigate();
  const idleTimeRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  const autoRefreshTimer = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation("loginTranslation");
  const locationInfo = useLocation();
  const findUrlString = locationInfo.pathname;
  sessionStorage.setItem("currentPath", findUrlString);
  //START - REDBALL CONFIG
  const { getAccessTokenSilently } = useAuth0();
  const nonQADomain = checkNonQADomain();
  //END - REDBALL CONFIG

  const onIdle = () => {
    setTimeLeft(100);
    setModalIsOpen(true);
    sessionTimeoutRef.current = setTimeout(() => {
      dispatch(Actions.setSessionExpired(true));
    }, 100000);
  };
  useEffect(() => {
    dispatch(Actions.setTimersRunning(true));
  }, []);

  useEffect(() => {
    if (props.timersRunning) {
      autoRefreshTimer.current = setInterval(() => {
        //Multitab logout
        //START - REDBALL CONFIG
        if (REDBALL_FEATURE_TOGGLE) {
          if (checkImpersonateUser()) {
            checkTkauth();
          }
          else if ((Cookies.get("cptoken") === undefined || Cookies.get("cptoken") === "") && sessionStorage.getItem("applicationLoaded") === "true") {
            dispatch(Actions.setLogout(true));
          }
        } else {
          checkTkauth();
        }
        //END - REDBALL CONFIG
      }, 2000);
      idleTimeRef.current.reset();
    } else {
      setModalIsOpen(false);
      clearTimeout(sessionTimeoutRef.current);
      clearInterval(autoRefreshTimer.current);
      idleTimeRef.current.pause();
    }
  }, [props.timersRunning]);

  useEffect(() => {
    if (props.sessionExpired || props.revealSessionExpired || props.logoutState || props.revealPermissionFallBack) {
      if (props.sessionExpired) {
        navigate("/sessionExpired");
      } else if (props.revealSessionExpired) {
        navigate("/revealSessionExpired");
      } else if (props.revealPermissionFallBack) {
        navigate("/permissionError");
      } else {
        navigate("/logout");
      }
    }
  }, [props.sessionExpired, props.revealSessionExpired, props.logoutState, props.revealPermissionFallBack]);

  const checkTkauth = () => {
    if ((Cookies.get("tkauth") === undefined || Cookies.get("tkauth") === "") && sessionStorage.getItem("applicationLoaded") === "true") {
      dispatch(Actions.setLogout(true));
    }
  }

  const stayActive = async () => {
    //START - REDBALL CONFIG
    if (REDBALL_FEATURE_TOGGLE && nonQADomain) {
      if (checkImpersonateUser()) {
        const response = await refreshToken();
        if (response?.response?.status === 401) {
          dispatch(Actions.setSessionExpired(true));
        } else {
          idleTimeRef.current.reset();
          clearTimeout(sessionTimeoutRef.current);
        }
      }
      else {
        const accessToken = await getAccessTokenSilently();
        Cookies.set("cptoken", accessToken);
        idleTimeRef.current.reset();
        clearTimeout(sessionTimeoutRef.current);
      }
    }
    else {
      const response = await refreshToken();
      if (response?.response?.status === 401) {
        dispatch(Actions.setSessionExpired(true));
      } else {
        idleTimeRef.current.reset();
        clearTimeout(sessionTimeoutRef.current);
      }
    }
    //END - REDBALL CONFIG
  };

  const logout = async () => {
    dispatch(Actions.setLogout(true));
  };

  useEffect(() => {
    let intervalId;
    if (timeLeft !== 0) {
      intervalId = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const handleSessionTimeOutModal = (value) => {
    setModalIsOpen(value);
    if (!value) {
      stayActive();
    }
  };

  return (
    <div>
      <Modal
        open={modalIsOpen}
        closeOnEsc={true}
        onEscKeyDown={() => handleSessionTimeOutModal(false)}
        onClose={() => handleSessionTimeOutModal(false)}
        showCloseIcon={false}
        closeOnOverlayClick={false}
        center
        role="alertdialog"
      >
        <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="16px" alignSelf="stretch">
          <Wrapper display="flex" justifyContent="space-between" width="100%">
            <TextWrapper fontWeight="bold" marginTop="0px" marginBottom="0px" fontSize="24px" width="90%" lineHeight="2rem" ariaLabel="Session time out" role="alert">
              {t("Session time out")}
            </TextWrapper>
            <Wrapper onClick={() => handleSessionTimeOutModal(false)}>
              <CPIcon id="close-icon" tabIndex="0" type="close" ariaLabel="close icon" outlineNotRequired={true} />
            </Wrapper>
          </Wrapper>
          <TextWrapper fontStyle="normal" fontWeight="400" fontSize="14px" lineHeight="20px">
            {t("Time left", { number: `${timeLeft}` })}{" "}
          </TextWrapper>
          <TextWrapper fontStyle="normal" fontWeight="400" fontSize="14px" lineHeight="20px">
            {t("Select")}
            <b>{t("CONTINUE SESSION")}</b>
            {t("to continue or")}
            <b>{t("LOG OUT ")}</b>
            {t("to end it now.")}
          </TextWrapper>
        </Wrapper>
        <Wrapper
          display="flex"
          justifyContent="flex-end"
          alignItems="flex-end"
          gap="16px"
          alignSelf="stretch"
          flexDirection={props.windowSize.width <= 480 ? "column-reverse" : ""}
        >
          <CustomisedTertiaryButton
            ariaLabel="Continue Session"
            id={"continue-session"}
            onClick={() => handleSessionTimeOutModal(false)}
            buttonWidth={props.windowSize.width <= MobileBreakPoint ? "100%" : "fit-content"}
            buttonHeight={"40px"}
            fontSize={"14px"}
          >
            {t("Continue Session")}
          </CustomisedTertiaryButton>

          <CPButton
            width="autoTight"
            id={"Leave-button"}
            buttonHeight={"40px"}
            buttonWidth={props.windowSize.width <= MobileBreakPoint ? "100%" : "fit-content"}
            ariaLabel="LOG OUT"
            boxButton={true}
            onClick={logout}
            style={{ height: "2.5rem" }}
            fontSize={"14px"}
          >
            {t("LOG OUT")}
          </CPButton>
        </Wrapper>
      </Modal>

      <IdleTimer ref={idleTimeRef} timeout={30 * 60 * 1000} onIdle={() => onIdle()} stopOnIdle={true}></IdleTimer>
    </div>
  );
}

IdleTimerContainer.propTypes = {
  timersRunning: PropTypes.bool,
  sessionExpired: PropTypes.bool,
  logoutState: PropTypes.bool,
  revealSessionExpired: PropTypes.bool,
  revealPermissionFallBack: PropTypes.bool,
  windowSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
};

export default connect(
  (state) => ({
    sessionExpired: state.ACCOUNT.sessionExpired,
    logoutState: state.ACCOUNT.logout,
    revealSessionExpired: state.ACCOUNT.revealSessionExpired,
    timersRunning: state.ACCOUNT.timersRunning,
    revealPermissionFallBack: state.ACCOUNT.revealPermissionFallBack,
    accountNumber: state.ACCOUNT.accountNumber,
    windowSize: state.ACCOUNT.windowSize,
  }),
  null
)(IdleTimerContainer);